<template>
    <div>
        <app-page-header></app-page-header>

        <v-container grid-list-xl class="box">
            <!-- Nalaganje pravic -->
            <v-layout v-if="parkingGatesLoading" row wrap align-center justify-center>
                <v-flex xs12 sm12 md10 lg10 xl8 class="text-center py-12">
                    <v-progress-circular indeterminate size="40" width="4" color="primary"></v-progress-circular>
                    <div class="parking-gates-message">Pridobivam podatke o zapornicah...</div>
                </v-flex>
            </v-layout>

            <!-- Napaka pri pridobivanju -->
            <v-layout v-else-if="parkingGatesError" row wrap align-center justify-center>
                <v-flex xs12 sm10 md10 lg10 xl8>
                    <v-card class="parking-gates-error-card" elevation="2">
                        <div class="parking-gates-error-icon">
                            <v-icon size="30" color="error">mdi-alert-circle-outline</v-icon>
                        </div>
                        <div class="parking-gates-error-content">
                            <div class="parking-gates-error-title">Podatkov ni bilo mogoče pridobiti</div>
                            <div class="parking-gates-error-text">
                                Pri pridobivanju dovoljenih zapornic je prišlo do napake.
                                Prosimo, poskusite ponovno.
                            </div>
                        </div>
                        <v-btn outlined color="error" class="parking-gates-error-button" @click="retryParametersData()">
                            <v-icon left small>mdi-refresh</v-icon>
                            Poskusi ponovno
                        </v-btn>
                    </v-card>
                </v-flex>
            </v-layout>

            <!-- Uporabnik nima dodeljenih zapornic -->
            <v-layout
                v-else-if="parkingGatesLoaded && allowedBarriers.length === 0"
                row wrap align-center justify-center
            >
                <v-flex xs12 sm12 md10 lg10 xl8>
                    <v-card class="no-access-card" elevation="2">
                        <v-icon size="48" color="grey lighten-1">mdi-boom-gate-outline</v-icon>
                        <div class="no-access-title">Zapornice niso dodeljene</div>
                        <div class="no-access-text">
                            Za vaš uporabniški račun trenutno ni dodeljena nobena zapornica za upravljanje.
                        </div>
                        <div class="no-access-hint">
                            Za dodelitev dostopa se obrnite na administratorja sistema.
                        </div>
                    </v-card>
                </v-flex>
            </v-layout>

            <!-- Zapornice -->
            <v-layout
                v-else-if="parkingGatesLoaded && allowedBarriers.length > 0"
                row wrap align-center justify-center
            >
                <v-flex xs12 sm12 md10 lg10 xl8>
                    <v-layout row wrap align-center justify-center>
                        <v-flex
                            v-for="barrier in allowedBarriers"
                            :key="barrier.id"
                            xs12 sm12 md6 lg6 xl6
                        >
                            <v-card class="gate-card" elevation="2">
                                <!-- HEADER -->
                                <div class="gate-card-header">
                                    <div class="gate-title">
                                        <div class="gate-icon">
                                            <v-icon size="28" color="primary">mdi-boom-gate</v-icon>
                                        </div>
                                        <div class="gate-title-text">
                                            <div class="gate-name">{{ barrier.name }}</div>
                                            <div class="gate-location">
                                                <v-icon x-small class="mr-1" color="grey">mdi-map-marker-outline</v-icon>
                                                {{ barrier.location }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="gate-header-actions">
                                        <v-tooltip
                                            v-if="barrier.updateDateTime !== null"
                                            :left="$vuetify.breakpoint.smAndUp"
                                            :bottom="$vuetify.breakpoint.smAndDown"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon v-bind="attrs" v-on="on" class="header-icon-btn">
                                                    <v-icon size="22" :color="deviceUpdateTimestampColor(barrier.updateTimestampDt)">
                                                        mdi-clock-outline
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Čas posodobitve: {{ barrier.updateDateTime }}</span>
                                        </v-tooltip>

                                        <v-tooltip :left="$vuetify.breakpoint.smAndUp" :bottom="$vuetify.breakpoint.smAndDown">
                                            <template v-slot:activator="{ on, attrs }">
                                                <div class="temperature-badge" v-bind="attrs" v-on="on">
                                                    <v-icon size="20" :color="socTempValueColor(barrier.socTemp)">mdi-thermometer</v-icon>
                                                    <span :class="['temperature-value', socTempValueColor(barrier.socTemp) + '--text']">
                                                        {{ formatTemperature(barrier.socTemp) }}
                                                    </span>
                                                </div>
                                            </template>
                                            <span>Temperatura (SoC): {{ formatTemperature(barrier.socTemp) }}</span>
                                        </v-tooltip>

                                        <v-btn icon class="header-icon-btn ml-1" @click="toggleBarrierCard(barrier.cardKey)">
                                            <v-icon size="24">
                                                {{ isCardOpen(barrier.cardKey) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                            </v-icon>
                                        </v-btn>
                                    </div>
                                </div>

                                <v-expand-transition>
                                    <div v-show="isCardOpen(barrier.cardKey)">
                                        <!-- STATUS -->
                                        <div class="gate-status-section">
                                            <div v-if="barrier.gatePosition !== null && barrier.gateConstantRise !== null">
                                                <div class="status-row">
                                                    <div class="status-info">
                                                        <div class="status-icon-box" :class="gateConditionColor(barrier.gatePosition) + '-soft'">
                                                            <v-icon size="30" :color="gateConditionColor(barrier.gatePosition)">mdi-boom-gate</v-icon>
                                                        </div>
                                                        <div class="status-text">
                                                            <div class="status-label">Pozicija zapornice</div>
                                                            <div class="status-description">Trenutno stanje</div>
                                                        </div>
                                                    </div>
                                                    <v-chip :color="gateConditionColor(barrier.gatePosition)" text-color="white" class="status-chip">
                                                        <v-icon left small>{{ gateConditionIcon(barrier.gatePosition) }}</v-icon>
                                                        {{ gateConditionText(barrier.gatePosition) }}
                                                    </v-chip>
                                                </div>

                                                <div class="status-row">
                                                    <div class="status-info">
                                                        <div class="status-icon-box" :class="gateConstantRiseColor(barrier.gateConstantRise) + '-soft'">
                                                            <v-icon size="30" :color="gateConstantRiseColor(barrier.gateConstantRise)">mdi-boom-gate-up</v-icon>
                                                        </div>
                                                        <div class="status-text">
                                                            <div class="status-label">Stalni dvig zapornice</div>
                                                            <div class="status-description">Način stalno odprte zapornice</div>
                                                        </div>
                                                    </div>
                                                    <v-chip :color="gateConstantRiseColor(barrier.gateConstantRise)" text-color="white" class="status-chip">
                                                        <v-icon left small>{{ gateConstantRiseIcon(barrier.gateConstantRise) }}</v-icon>
                                                        {{ gateConstantRiseText(barrier.gateConstantRise) }}
                                                    </v-chip>
                                                </div>
                                            </div>

                                            <div v-else class="status-loading">
                                                <v-progress-circular indeterminate size="30" width="3" color="primary"></v-progress-circular>
                                                <span>Pridobivam stanje zapornice...</span>
                                            </div>
                                        </div>

                                        <!-- ACTIONS -->
                                        <div class="gate-actions">
                                            <div class="actions-header">
                                                <div class="actions-title">
                                                    <v-icon size="22" class="mr-2" color="grey darken-2">mdi-car</v-icon>
                                                    Upravljanje zapornice
                                                </div>
                                                <div class="actions-subtitle">Dvig, spust ali stalni dvig</div>
                                            </div>

                                            <div class="gate-buttons">
                                                <v-btn
                                                    depressed
                                                    class="gate-action-btn dvig"
                                                    :disabled="isLoading(barrier.loadingUp) || disabled"
                                                    :loading="isLoading(barrier.loadingUp)"
                                                    @click="send({ device_id: barrier.deviceId, event_type_id: 1, key: barrier.ascentKey }, barrier.loadingUp, 'Dvig ' + barrier.name.toLowerCase() + ' je uspel.')"
                                                >
                                                    <v-icon left>mdi-arrow-up-bold</v-icon>
                                                    Dvig
                                                </v-btn>

                                                <v-btn
                                                    depressed
                                                    class="gate-action-btn spust"
                                                    :disabled="isLoading(barrier.loadingDown) || disabled"
                                                    :loading="isLoading(barrier.loadingDown)"
                                                    @click="send({ device_id: barrier.deviceId, event_type_id: 3, key: barrier.descentKey }, barrier.loadingDown, 'Spust ' + barrier.name.toLowerCase() + ' je uspel.')"
                                                >
                                                    <v-icon left>mdi-arrow-down-bold</v-icon>
                                                    Spust
                                                </v-btn>

                                                <v-btn
                                                    depressed
                                                    class="gate-action-btn stalni_dvig"
                                                    :disabled="isLoading(barrier.loadingPermanent) || disabled"
                                                    :loading="isLoading(barrier.loadingPermanent)"
                                                    @click="send({ device_id: barrier.deviceId, event_type_id: 2, key: barrier.permanentAscentKey }, barrier.loadingPermanent, 'Stalni dvig ' + barrier.name.toLowerCase() + ' je uspel.')"
                                                >
                                                    <v-icon left>mdi-lock-open-variant</v-icon>
                                                    Stalni dvig
                                                </v-btn>
                                            </div>
                                        </div>
                                    </div>
                                </v-expand-transition>
                            </v-card>
                        </v-flex>
                    </v-layout>
                </v-flex>
            </v-layout>
        </v-container>
    </div>
</template>

<script>
import moment from 'moment'
import { parseStatusStrV3 } from '@/helpers/utilities'

export default {
    name: 'ParkingBarriers',

    data: () => ({
        card1: true,
        card2: true,

        loading1: false,
        loading2: false,
        loading3: false,
        loading4: false,
        loading5: false,
        loading6: false,

        disabled: false,
        interval: null,
        devices: [],

        userParkingGates: [],
        parkingGatesLoading: true,
        parkingGatesLoaded: false,
        parkingGatesError: false,

        pg_device_id_1: process.env.VUE_APP_DATA_PARKING_GATE_DEVICE_ID_1,
        pg_ascent_key_1: process.env.VUE_APP_DATA_PARKING_GATE_ASCENT_KEY_1,
        pg_descent_key_1: process.env.VUE_APP_DATA_PARKING_GATE_DESCENT_KEY_1,
        pg_permanent_ascent_key_1: process.env.VUE_APP_DATA_PARKING_GATE_PERMANENT_ASCENT_KEY_1,
        pg_device_id_1_gate_position: null,
        pg_device_id_1_gate_constant_rise: null,
        pg_device_id_1_update_date_time: null,

        pg_device_id_2: process.env.VUE_APP_DATA_PARKING_GATE_DEVICE_ID_2,
        pg_ascent_key_2: process.env.VUE_APP_DATA_PARKING_GATE_ASCENT_KEY_2,
        pg_descent_key_2: process.env.VUE_APP_DATA_PARKING_GATE_DESCENT_KEY_2,
        pg_permanent_ascent_key_2: process.env.VUE_APP_DATA_PARKING_GATE_PERMANENT_ASCENT_KEY_2,
        pg_device_id_2_gate_position: null,
        pg_device_id_2_gate_constant_rise: null,
        pg_device_id_2_update_date_time: null,

        device_1_update_timestamp: null,
        device_1_update_timestamp_dt: null,
        device_2_update_timestamp: null,
        device_2_update_timestamp_dt: null,
        device_1_soc_temp: null,
        device_2_soc_temp: null,

        statuses: [],
        device_1: process.env.VUE_APP_DEVICE_1_UUID,
        device_2: process.env.VUE_APP_DEVICE_2_UUID
    }),

    computed: {
        barriers() {
            return [
                {
                    id: 1,
                    name: 'Vhodna zapornica',
                    location: 'Kranjska Gora',
                    cardKey: 'card1',
                    deviceId: this.pg_device_id_1,
                    ascentKey: this.pg_ascent_key_1,
                    descentKey: this.pg_descent_key_1,
                    permanentAscentKey: this.pg_permanent_ascent_key_1,
                    gatePosition: this.pg_device_id_1_gate_position,
                    gateConstantRise: this.pg_device_id_1_gate_constant_rise,
                    updateDateTime: this.pg_device_id_1_update_date_time,
                    updateTimestampDt: this.device_1_update_timestamp_dt,
                    socTemp: this.device_1_soc_temp,
                    loadingUp: 'loading1',
                    loadingDown: 'loading2',
                    loadingPermanent: 'loading3'
                },
                {
                    id: 2,
                    name: 'Izhodna zapornica',
                    location: 'Kranjska Gora',
                    cardKey: 'card2',
                    deviceId: this.pg_device_id_2,
                    ascentKey: this.pg_ascent_key_2,
                    descentKey: this.pg_descent_key_2,
                    permanentAscentKey: this.pg_permanent_ascent_key_2,
                    gatePosition: this.pg_device_id_2_gate_position,
                    gateConstantRise: this.pg_device_id_2_gate_constant_rise,
                    updateDateTime: this.pg_device_id_2_update_date_time,
                    updateTimestampDt: this.device_2_update_timestamp_dt,
                    socTemp: this.device_2_soc_temp,
                    loadingUp: 'loading4',
                    loadingDown: 'loading5',
                    loadingPermanent: 'loading6'
                }
            ]
        },

        allowedBarriers() {
            return this.barriers.filter(barrier => this.hasParkingGateAccess(barrier.deviceId))
        }
    },

    methods: {
        isCardOpen(cardKey) {
            return this[cardKey]
        },

        toggleBarrierCard(cardKey) {
            this[cardKey] = !this[cardKey]
        },

        isLoading(loadingKey) {
            return this[loadingKey]
        },

        resetLoadings() {
            for (let i = 1; i <= 6; i++) {
                this['loading' + i] = false
            }
        },

        formatTemperature(value) {
            if (value === null || value === '') {
                return 'N/A'
            }
            return value + ' °C'
        },

        deviceUpdateTimestampColor(deviceUpdateTimestamp) {
            if (deviceUpdateTimestamp !== null) {
                const currentTimestamp = moment().format('YYYY-MM-DD HH:mm:ss')
                const calculation = (new Date(currentTimestamp) - new Date(deviceUpdateTimestamp)) / 1000

                if (calculation > 65) {
                    return 'error'
                }
                return 'success'
            }
            return 'error'
        },

        socTempValueColor(value) {
            if (value === null || value === '') return 'grey'
            if (value >= 67) return 'error'
            if (value > 57) return 'warning'
            return 'success'
        },

        gateConditionText(status) {
            return status == 1 ? 'Odprta' : 'Zaprta'
        },

        gateConstantRiseText(status) {
            return status == 1 ? 'Aktiven' : 'Ni aktiven'
        },

        gateConditionIcon(status) {
            return status == 1 ? 'mdi-lock-open-variant' : 'mdi-lock'
        },

        gateConstantRiseIcon(status) {
            return status == 1 ? 'mdi-check' : 'mdi-cancel'
        },

        gateConditionColor(status) {
            return status == 1 ? 'success' : 'error'
        },

        gateConstantRiseColor(status) {
            return status == 1 ? 'success' : 'error'
        },

        showSnackbar(text) {
            this.$store.commit('snackbarDisplay', false)
            this.$store.commit('snackbarMode', 'multi-line')
            this.$store.commit('snackbarText', text)
            this.$store.commit('snackbarTimeout', 7000)
            this.$store.commit('snackbarDisplay', true)
        },

        send(data, loadingKey, successText) {
            const payload = {
                topic: 'parking-barriers',
                application_uuid: process.env.VUE_APP_UUID,
                payload: data
            }

            this[loadingKey] = true

            if (process.env.VUE_APP_DATA_DEBUG == true) {
                this.showSnackbar(successText)
                this[loadingKey] = false
                this.disabled = false
                return
            }

            this.disabled = true

            this.$store.dispatch('PUBLISH', payload)
                .then(() => {
                    this.showSnackbar(successText)
                })
                .catch(error => {
                    window.console.error('### ParkingBarriers@send ###', error)
                    this.showSnackbar('Pri upravljanju zapornice je prišlo do napake. Prosimo poskusite ponovno.')
                })
                .finally(() => {
                    this[loadingKey] = false
                    setTimeout(() => {
                        this.disabled = false
                    }, 4000)
                })
        },

        fetchStatuses() {
            const payload = []

            if (this.device_1 !== null && this.device_1 !== '') {
                payload.push(this.device_1)
            }

            if (this.device_2 !== null && this.device_2 !== '') {
                payload.push(this.device_2)
            }

            this.$store.commit('SET_PROGRESS', true)

            this.$store.dispatch('DEVICE_STATUS', { devices: payload })
                .then(response => {
                    this.setStatuses(response.data.payload)
                })
                .catch(error => {
                    window.console.error('### ParkingBarriers@fetchStatuses ###', error)
                })
                .finally(() => {
                    this.$store.commit('SET_PROGRESS', false)
                })
        },

        setStatuses(payload) {
            payload.forEach(item => {
                // Obe zapornici sta na isti napravi; status se bere iz kanala 1 in 2.
                if (process.env.VUE_APP_DEVICE_1_UUID == item.device_uuid) {
                    const device1Entrance = parseStatusStrV3(item.status, 1)
                    const device1Exit = parseStatusStrV3(item.status, 2)

                    this.pg_device_id_1_gate_position = device1Entrance.gate_position
                    this.pg_device_id_1_gate_constant_rise = device1Entrance.gate_constant_rise
                    this.pg_device_id_1_update_date_time = item.updated_at_formatted
                    this.device_1_update_timestamp = item.updated_at_formatted
                    this.device_1_update_timestamp_dt = item.updated_at
                    this.device_1_soc_temp = item.soc_temp

                    this.pg_device_id_2_gate_position = device1Exit.gate_position
                    this.pg_device_id_2_gate_constant_rise = device1Exit.gate_constant_rise
                    this.pg_device_id_2_update_date_time = item.updated_at_formatted
                    this.device_2_update_timestamp = item.updated_at_formatted
                    this.device_2_update_timestamp_dt = item.updated_at
                    this.device_2_soc_temp = item.soc_temp
                }
            })
        },

        hasParkingGateAccess(parkingGateId) {
            return this.userParkingGates.includes(Number(parkingGateId))
        },

        fetchParametersData() {
            this.parkingGatesLoading = true
            this.parkingGatesLoaded = false
            this.parkingGatesError = false

            return this.$store.dispatch('PARAMETERS_DATA')
                .then(response => {
                    this.userParkingGates = response.data.userParkingGates || []
                    this.userParkingGates = this.userParkingGates.map(id => Number(id))
                    this.parkingGatesLoaded = true
                })
                .catch(error => {
                    window.console.error('### ParkingBarriers@fetchParametersData ###', error)
                    this.userParkingGates = []
                    this.parkingGatesError = true
                })
                .finally(() => {
                    this.parkingGatesLoading = false
                })
        },

        retryParametersData() {
            this.fetchParametersData().then(() => {
                if (this.parkingGatesLoaded && this.allowedBarriers.length > 0) {
                    this.fetchStatuses()
                }
            })
        }
    },

    created() {
        this.$store.dispatch('ROUTE_NAME', this.$route.name)

        this.fetchParametersData().then(() => {
            if (this.parkingGatesLoaded && this.allowedBarriers.length > 0) {
                this.fetchStatuses()
            }
        })

        this.interval = setInterval(() => {
            if (this.parkingGatesLoaded && this.allowedBarriers.length > 0) {
                this.fetchStatuses()
            }
        }, process.env.VUE_APP_DEVICE_STATUS_REFRESH_INTERVAL || 8000)
    },

    beforeDestroy() {
        if (this.interval) {
            clearInterval(this.interval)
            this.interval = null
        }
    }
}
</script>

<style scoped>
.gate-card {
    overflow: hidden;
    margin-bottom: 6px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s ease;
}

.gate-card:hover {
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.11) !important;
}

.gate-card-header {
    min-height: 76px;
    padding: 12px 14px 12px 18px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #ffffff;
    border-bottom: 1px solid #eeeeee;
}

.gate-title { min-width: 0; display: flex; align-items: center; }
.gate-icon {
    width: 48px; height: 48px; margin-right: 13px; display: flex;
    align-items: center; justify-content: center; flex-shrink: 0;
    border-radius: 8px; background: rgba(56, 119, 53, 0.09);
}
.gate-title-text { min-width: 0; }
.gate-name {
    overflow: hidden; font-size: 16px; font-weight: 500; line-height: 1.35;
    color: #303030; text-overflow: ellipsis; white-space: nowrap;
}
.gate-location { margin-top: 5px; display: flex; align-items: center; font-size: 12px; color: #8b8b8b; }
.gate-header-actions { display: flex; align-items: center; margin-left: 12px; flex-shrink: 0; }
.header-icon-btn { width: 40px !important; height: 40px !important; }
.temperature-badge {
    min-width: 66px; height: 36px; padding: 0 9px; display: flex; align-items: center;
    justify-content: center; border-radius: 18px; background: #f5f5f5; cursor: default;
}
.temperature-value { margin-left: 3px; font-size: 12px; font-weight: 500; }

.gate-status-section { padding: 8px 20px; background: #ffffff; }
.status-row {
    min-height: 82px; display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid #f0f0f0;
}
.status-row:last-child { border-bottom: none; }
.status-info { min-width: 0; display: flex; align-items: center; }
.status-icon-box {
    width: 48px; height: 48px; margin-right: 14px; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; border-radius: 8px;
}
.success-soft { background: rgba(76, 175, 80, 0.10); }
.error-soft { background: rgba(244, 67, 54, 0.09); }
.warning-soft { background: rgba(255, 152, 0, 0.10); }
.status-text { min-width: 0; }
.status-label { font-size: 15px; font-weight: 500; color: #3d3d3d; }
.status-description { margin-top: 4px; font-size: 12px; color: #989898; }
.status-chip { min-width: 112px; margin-left: 16px; justify-content: center; font-size: 12px !important; font-weight: 500; }
.status-loading {
    min-height: 164px; display: flex; align-items: center; justify-content: center;
    gap: 12px; font-size: 13px; color: #777777;
}

.gate-actions { padding: 18px 20px 20px; background: #fafafa; border-top: 1px solid #eeeeee; }
.actions-header { margin-bottom: 14px; }
.actions-title { display: flex; align-items: center; font-size: 14px; font-weight: 500; color: #474747; }
.actions-subtitle { margin-top: 4px; margin-left: 30px; font-size: 11px; color: #9a9a9a; }
.gate-buttons { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
.gate-action-btn {
    min-height: 40px; text-transform: none !important; letter-spacing: 0 !important;
    font-size: 13px; font-weight: 500; border-radius: 6px;
}
.dvig { background: #4ba36b !important; color: #ffffff !important; }
.spust { background: #e79e42 !important; color: #ffffff !important; }
.stalni_dvig { grid-column: auto; background: #ce5959 !important; color: #ffffff !important; }

.parking-gates-message { margin-top: 15px; font-size: 13px; color: #777777; }
.no-access-card {
    margin-top: 10px; padding: 40px 30px; text-align: center; border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}
.no-access-title { margin-top: 16px; margin-bottom: 8px; font-size: 18px; font-weight: 500; color: #424242; }
.no-access-text { max-width: 480px; margin: 0 auto; font-size: 14px; line-height: 1.6; color: #777777; }
.no-access-hint { margin-top: 10px; font-size: 12px; color: #9e9e9e; }
.parking-gates-error-card {
    min-height: 100px; padding: 20px 24px; display: flex; align-items: center;
    border-radius: 8px; border-left: 4px solid #f44336;
}
.parking-gates-error-icon {
    width: 52px; height: 52px; margin-right: 18px; display: flex; align-items: center;
    justify-content: center; flex-shrink: 0; border-radius: 50%; background: rgba(244, 67, 54, 0.08);
}
.parking-gates-error-content { min-width: 0; flex: 1; }
.parking-gates-error-title { margin-bottom: 4px; font-size: 15px; font-weight: 500; color: #424242; }
.parking-gates-error-text { font-size: 13px; line-height: 1.5; color: #777777; }
.parking-gates-error-button {
    margin-left: 24px; flex-shrink: 0; text-transform: none !important; letter-spacing: 0 !important;
}

@media (max-width: 1263px) {
    .gate-name { font-size: 15px; }
    .gate-icon { width: 44px; height: 44px; margin-right: 10px; }
    .temperature-badge { min-width: 58px; }
}

@media (max-width: 600px) {
    .gate-card-header { min-height: 70px; padding-left: 13px; padding-right: 8px; }
    .gate-icon { width: 42px; height: 42px; }
    .gate-name { font-size: 14px; white-space: normal; }
    .gate-location { font-size: 11px; }
    .temperature-badge { min-width: auto; padding: 0 6px; }
    .temperature-value { display: none; }
    .header-icon-btn { width: 36px !important; height: 36px !important; }
    .gate-status-section { padding-left: 14px; padding-right: 14px; }
    .status-row { min-height: 76px; }
    .status-icon-box { width: 42px; height: 42px; margin-right: 10px; }
    .status-label { font-size: 13px; }
    .status-description { display: none; }
    .status-chip { min-width: 92px; margin-left: 8px; font-size: 11px !important; }
    .gate-actions { padding-left: 14px; padding-right: 14px; }
    .gate-buttons { grid-template-columns: 1fr; }
    .stalni_dvig { grid-column: auto; }
    .parking-gates-error-card { align-items: flex-start; flex-wrap: wrap; padding: 18px; }
    .parking-gates-error-icon { width: 44px; height: 44px; margin-right: 12px; }
    .parking-gates-error-content { width: calc(100% - 56px); }
    .parking-gates-error-button { width: 100%; margin-top: 16px; margin-left: 0; }
}
</style>
